import { Link } from "react-router-dom";
import Seo from "@/components/Seo";
import PageHeader from "@/components/PageHeader";
import FinalCTA from "@/components/sections/FinalCTA";
import NewsletterForm from "@/components/NewsletterForm";
import { Reveal, Stagger, StaggerItem } from "@/components/motion";
import { SectionHeading } from "@/components/primitives";
import Icon from "@/components/Icon";
import { RESOURCES } from "@/lib/site";
import { ARTICLES } from "@/lib/articles";
import { HANDBOOK } from "@/lib/handbook";
import { ArrowUpRight, BookOpen } from "lucide-react";

export default function Resources() {
  const [featured, ...rest] = ARTICLES;
  return (
    <>
      <Seo
        title="Resources — WorkSpace Handbook, Blog & Articles"
        description="Guides, playbooks and product stories for Stupeco WorkSpace — how to run a connected business."
        path="/resources"
      />
      <PageHeader
        eyebrow="Resources"
        title="WorkSpace Handbook, blog & articles."
        subtitle="Practical guides to get the most out of WorkSpace — plus longer-form founder writing."
      />

      {/* WorkSpace Handbook — practical launch guides */}
      <section className="border-b border-border py-20 sm:py-24" data-testid="workspace-handbook-section">
        <div className="mx-auto max-w-7xl px-5 sm:px-8">
          <SectionHeading
            eyebrow="WorkSpace Handbook"
            title="Learn WorkSpace in a lunch break."
            subtitle="Focused, no-fluff guides for the modules you'll use every day."
          />
          <Stagger className="mt-14 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {HANDBOOK.map((h) => (
              <StaggerItem key={h.slug} className="h-full">
                <Link
                  to={`/resources/${h.slug}`}
                  data-testid={`handbook-card-${h.slug}`}
                  className="card-hover group flex h-full flex-col gap-3 rounded-2xl border border-border bg-card p-6 hover:border-foreground/20"
                >
                  <div className="flex items-center gap-2 text-xs">
                    <span className="inline-flex h-8 w-8 items-center justify-center rounded-lg bg-brand-teal/10 text-brand-teal"><BookOpen className="h-4 w-4" /></span>
                    <span className="rounded-full bg-muted px-2.5 py-1 font-semibold text-muted-foreground ring-1 ring-border">{h.category}</span>
                    <span className="ml-auto text-muted-foreground">{h.read}</span>
                  </div>
                  <h3 className="mt-1 font-display text-base font-semibold tracking-tight">{h.title}</h3>
                  <p className="flex-1 text-sm leading-relaxed text-muted-foreground">{h.excerpt}</p>
                  <span className="mt-2 inline-flex items-center gap-1.5 text-sm font-medium text-foreground">
                    Read guide <ArrowUpRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                  </span>
                </Link>
              </StaggerItem>
            ))}
          </Stagger>
        </div>
      </section>

      {/* Featured + list */}
      <section className="border-b border-border py-20 sm:py-24">
        <div className="mx-auto max-w-7xl px-5 sm:px-8">
          <Reveal className="group">
            <Link to={`/resources/${featured.slug}`} data-testid="featured-article" className="grid overflow-hidden rounded-3xl border border-border bg-card transition-colors hover:border-foreground/20 lg:grid-cols-2">
              <div className="relative h-56 overflow-hidden lg:h-auto">
                <img
                  src={featured.hero}
                  alt={featured.title}
                  loading="lazy"
                  className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-[1.04]"
                />
              </div>
              <div className="flex flex-col justify-center p-8 sm:p-12">
                <div className="flex items-center gap-3 text-xs">
                  <span className="rounded-full bg-brand-teal/12 px-2.5 py-1 font-semibold text-brand-teal ring-1 ring-brand-teal/25">{featured.category}</span>
                  <span className="text-muted-foreground">{featured.read}</span>
                </div>
                <h2 className="mt-4 font-display text-2xl font-semibold tracking-tight sm:text-3xl">{featured.title}</h2>
                <p className="mt-3 text-sm leading-relaxed text-muted-foreground sm:text-base">{featured.excerpt}</p>
                <span className="mt-6 inline-flex items-center gap-1.5 text-sm font-medium text-foreground">
                  Read article <ArrowUpRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                </span>
              </div>
            </Link>
          </Reveal>

          <Stagger className="mt-8 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {rest.map((a) => (
              <StaggerItem key={a.slug} className="h-full">
                <Link
                  to={`/resources/${a.slug}`}
                  data-testid={`article-card-${a.slug}`}
                  className="card-hover group flex h-full flex-col rounded-2xl border border-border bg-card p-6 hover:border-foreground/20"
                >
                  <div className="flex items-center gap-3 text-xs">
                    <span className="rounded-full bg-muted px-2.5 py-1 font-semibold text-muted-foreground ring-1 ring-border">{a.category}</span>
                    <span className="text-muted-foreground">{a.read}</span>
                  </div>
                  <h3 className="mt-4 font-display text-lg font-semibold tracking-tight">{a.title}</h3>
                  <p className="mt-2 flex-1 text-sm leading-relaxed text-muted-foreground">{a.excerpt}</p>
                  <span className="mt-5 inline-flex items-center gap-1.5 text-sm font-medium text-foreground">
                    Read <ArrowUpRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                  </span>
                </Link>
              </StaggerItem>
            ))}
          </Stagger>
        </div>
      </section>

      {/* Resource categories */}
      <section className="border-b border-border py-20 sm:py-24">
        <div className="mx-auto max-w-7xl px-5 sm:px-8">
          <SectionHeading eyebrow="More resources" title="Everything to help you get the most from Workspace." />
          <Stagger className="mt-14 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
            {RESOURCES.map((r) => (
              <StaggerItem key={r.title} className="h-full">
                <div className="flex h-full flex-col gap-4 rounded-2xl border border-border bg-card p-6">
                  <div className="inline-flex h-11 w-11 items-center justify-center rounded-xl bg-muted/60 text-brand-teal">
                    <Icon name={r.icon} className="h-5 w-5" />
                  </div>
                  <div className="flex items-center gap-2">
                    <h3 className="font-display text-base font-semibold tracking-tight">{r.title}</h3>
                    <span className="rounded-full bg-muted px-2 py-0.5 text-[10px] font-semibold text-muted-foreground ring-1 ring-border">{r.tag}</span>
                  </div>
                  <p className="text-sm leading-relaxed text-muted-foreground">{r.desc}</p>
                </div>
              </StaggerItem>
            ))}
          </Stagger>

          <Reveal className="mt-14 flex flex-col items-start justify-between gap-6 rounded-3xl border border-border bg-card p-8 sm:flex-row sm:items-center sm:p-10">
            <div>
              <h3 className="font-display text-xl font-semibold tracking-tight">Get new articles in your inbox</h3>
              <p className="mt-1 text-sm text-muted-foreground">Founder stories and product updates. No spam.</p>
            </div>
            <div className="w-full sm:max-w-sm">
              <NewsletterForm source="/resources" compact />
            </div>
          </Reveal>
        </div>
      </section>
      <FinalCTA />
    </>
  );
}
